import React, { useEffect, useState, useRef } from 'react';
import './CSS/Home.css';
import ProductCarousel from '../Components/Slider/Slider';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';

function Home({ setPageReady }) {
  const [categories, setCategories] = useState([]);
  const [dashboardSettings, setDashboardSettings] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [structureReady, setStructureReady] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const showText = structureReady;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const swiperRef = useRef(null);
  const videoRefs = useRef([]);
  const videoTimes = useRef([]);

  useEffect(() => {
    setStructureReady(true);
  }, []);

  useEffect(() => {
    if (!structureReady) return;

    const fetchSettings = async () => {
      try {
        const res = await fetch(`${backendUrl}/dashboard-settings`);
        const data = await res.json();
        if (data.success && data.settings) {
          if (data.settings.mainMedia?.length) {
            setMediaItems(data.settings.mainMedia);
          }
          setDashboardSettings(data.settings);
        }
      } catch (err) {
        console.error('Failed to load dashboard settings', err);
      } finally {
        setPageReady(true);
        setMediaReady(true);
      }
    };

    fetchSettings();
  }, [structureReady, backendUrl, setPageReady]);

  useEffect(() => {
    if (mediaReady && swiperRef.current?.swiper) {
      const swiper = swiperRef.current.swiper;
      swiper.update();
      swiper.slideToLoop(0, 0);
    }
  }, [mediaReady]);

  useEffect(() => {
    const fetchCategoriesWithProducts = async () => {
      try {
        const response = await fetch(`${backendUrl}/category/categories-with-products`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        const result = await response.json();
        if (result.success) {
          const sortedCategories = result.categories.sort((a, b) => {
            if (a.name === 'featured') return -1;
            if (b.name === 'featured') return 1;
            return 0;
          });
          setCategories(sortedCategories);
        }
      } catch (error) {
        console.error('Error fetching categories with products:', error);
      }
    };
    fetchCategoriesWithProducts();
  }, [backendUrl]);

  const playVideoOnSlideChange = (swiper) => {
    const activeIndex = swiper?.activeIndex;
    if (typeof activeIndex !== 'undefined') {
      const currentVideo = videoRefs.current[activeIndex];
      const previousTime = videoTimes.current[activeIndex] || 0;

      if (currentVideo) {
        currentVideo.currentTime = previousTime;
        currentVideo.play().catch((error) => {
          console.error('Autoplay failed:', error);
        });
      }
    }
  };

  const storeVideoTimeOnSlideChange = (swiper) => {
    const previousIndex = swiper.previousIndex;
    if (typeof previousIndex !== 'undefined') {
      const previousVideo = videoRefs.current[previousIndex];
      if (previousVideo) {
        videoTimes.current[previousIndex] = previousVideo.currentTime;
      }
    }
  };

  return (
    <div className="Home">
      <div className="video-slider-container">
        <Swiper
		  modules={[Pagination]}
		  spaceBetween={0}
		  slidesPerView={1}
		  loop={true}
		  pagination={{ clickable: true }}
		  onSwiper={(swiper) => {
			swiperRef.current = swiper;
		  }}
		  onSlideChange={(swiper) => {
			storeVideoTimeOnSlideChange(swiper);
			playVideoOnSlideChange(swiper);
		  }}
		>
		  {(mediaReady && mediaItems.length > 0) ? (
			mediaItems.map((media, index) => (
			  <SwiperSlide key={media.url}>
				<div className="video-background fade-in">
				  {media.type === 'video' ? (
					<video
					  ref={(el) => (videoRefs.current[index] = el)}
					  loop
					  muted
					  preload="metadata"
					  playsInline
					  controls={false}
					  autoPlay={index === 0}
					  onCanPlayThrough={(e) => e.currentTarget.classList.add('loaded')}
					  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
					>
					  <source src={media.url} type="video/mp4" />
					  Your browser does not support the video tag.
					</video>
				  ) : media.url.startsWith('#') ? (
					<div
					  className="image-slide loaded"
					  style={{ backgroundColor: media.url }}
					/>
				  ) : (
					<img
					  src={media.url}
					  alt={`media-${index}`}
					  className="image-slide fade-in"
					  onLoad={(e) => e.currentTarget.classList.add('loaded')}
					/>
				  )}
				</div>
			  </SwiperSlide>
			))
		  ) : (
			<SwiperSlide key="placeholder">
			  <div className="video-background placeholder-slide">
				<div className="spinner" />
			  </div>
			</SwiperSlide>
		  )}
		</Swiper>

        {showText && (
          <div className="content fade-in">
            <h1 className="content-title">{dashboardSettings?.title}</h1>
            <p className="content-subtitle">{dashboardSettings?.subtitle}</p>
          </div>
        )}
      </div>

      <div className="carousel-section">
        {categories.map((category) => (
          <ProductCarousel
            key={category._id}
            category={category.name}
            displayName={category.displayName}
            products={category.products}
          />
        ))}
      </div>
    </div>
  );
}

export default Home;
